import type { TypedWrappedFieldProps } from "redux-form";

import { Box } from "@braintrust/braintrust-ui-components";
import { RATE_OPTIONS } from "@js/apps/jobs/apps/create-job/constants";
import { DollarNumberField } from "@js/forms/fields/number/number";
import { assertUnreachable } from "@js/utils";

import { RATE_FIELDS_KEYS } from "../constants";

import { RateCustomField } from "./rate-custom-field";

type AmountFieldProps = {
  payment_type: TypedWrappedFieldProps<EnumType<typeof ENUMS.JobPaymentType>>;
  fixed_rate: TypedWrappedFieldProps<string>;
  min_rate: TypedWrappedFieldProps<string>;
  max_rate: TypedWrappedFieldProps<string>;
  min_annual_rate: TypedWrappedFieldProps<string>;
  max_annual_rate: TypedWrappedFieldProps<string>;
  hourly_rate: TypedWrappedFieldProps<string>;
  custom_min_rate: TypedWrappedFieldProps<string>;
  custom_max_rate: TypedWrappedFieldProps<string>;
  min_monthly_rate: TypedWrappedFieldProps<string>;
  max_monthly_rate: TypedWrappedFieldProps<string>;
};

export const AmountField = ({
  payment_type,
  fixed_rate,
  min_rate,
  max_rate,
  min_annual_rate,
  max_annual_rate,
  hourly_rate,
  custom_min_rate,
  custom_max_rate,
  min_monthly_rate,
  max_monthly_rate,
}: AmountFieldProps) => {
  const selectedPaymentType: EnumType<typeof ENUMS.JobPaymentType> =
    payment_type.input.value;

  switch (selectedPaymentType) {
    case ENUMS.JobPaymentType.HOURLY: {
      return (
        <RateCustomField
          hourly_rate={hourly_rate}
          min_rate={min_rate}
          max_rate={max_rate}
          rateOptions={RATE_OPTIONS}
          custom_min_rate={custom_min_rate}
          custom_max_rate={custom_max_rate}
        />
      );
    }
    case ENUMS.JobPaymentType.FIXED_PRICE: {
      return (
        <DollarNumberField
          hideError
          id={RATE_FIELDS_KEYS.fixed_rate}
          name={RATE_FIELDS_KEYS.fixed_rate}
          {...fixed_rate}
          placeholder="100,000"
          sx={{
            maxWidth: "155px",
            ".MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
            "& input::placeholder": {
              color: "var(--grey-3)",
            },
          }}
          slotProps={{
            htmlInput: {
              style: { padding: "20px" },
              "aria-label": "fixed price salary",
            },
          }}
        />
      );
    }
    case ENUMS.JobPaymentType.FIXED_PROJECT: {
      return (
        <DollarNumberField
          hideError
          id={RATE_FIELDS_KEYS.fixed_rate}
          name={RATE_FIELDS_KEYS.fixed_rate}
          {...fixed_rate}
          placeholder="1,000"
          sx={{
            maxWidth: "155px",
            ".MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
            "& input::placeholder": {
              color: "var(--grey-3)",
            },
          }}
          slotProps={{
            htmlInput: {
              style: { padding: "20px" },
              "aria-label": "fixed project salary",
            },
          }}
        />
      );
    }
    case ENUMS.JobPaymentType.PER_TASK: {
      return (
        <DollarNumberField
          hideError
          id={RATE_FIELDS_KEYS.fixed_rate}
          name={RATE_FIELDS_KEYS.fixed_rate}
          {...fixed_rate}
          placeholder="100"
          sx={{
            maxWidth: "155px",
            ".MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
            "& input::placeholder": {
              color: "var(--grey-3)",
            },
          }}
          slotProps={{
            htmlInput: {
              style: { padding: "20px" },
              "aria-label": "salary per task",
            },
          }}
        />
      );
    }
    case ENUMS.JobPaymentType.MONTHLY: {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            maxWidth: "440px",
          }}
        >
          <DollarNumberField
            hideError
            id={RATE_FIELDS_KEYS.min_monthly_rate}
            name={RATE_FIELDS_KEYS.min_monthly_rate}
            {...min_monthly_rate}
            placeholder="10,000"
            size="large"
            slotProps={{
              htmlInput: { "aria-label": "minimum monthly salary" },
            }}
          />
          <Box sx={{ mx: 2 }}>to</Box>
          <DollarNumberField
            hideError
            id={RATE_FIELDS_KEYS.max_monthly_rate}
            name={RATE_FIELDS_KEYS.max_monthly_rate}
            {...max_monthly_rate}
            placeholder="15,000"
            size="large"
            slotProps={{
              htmlInput: { "aria-label": "maximum monthly salary" },
            }}
          />
        </Box>
      );
    }

    case ENUMS.JobPaymentType.ANNUAL: {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            maxWidth: "440px",
          }}
        >
          <DollarNumberField
            hideError
            id={RATE_FIELDS_KEYS.min_annual_rate}
            name={RATE_FIELDS_KEYS.min_annual_rate}
            {...min_annual_rate}
            placeholder="100,000"
            size="large"
            slotProps={{ htmlInput: { "aria-label": "minimum annual salary" } }}
          />
          <Box sx={{ mx: 2 }}>to</Box>
          <DollarNumberField
            hideError
            id={RATE_FIELDS_KEYS.max_annual_rate}
            name={RATE_FIELDS_KEYS.max_annual_rate}
            {...max_annual_rate}
            placeholder="150,000"
            size="large"
            slotProps={{ htmlInput: { "aria-label": "maximum annual salary" } }}
          />
        </Box>
      );
    }
    default: {
      assertUnreachable(selectedPaymentType);
      return null;
    }
  }
};
